import {InputDate} from '@/components/commons/input-date/InputDate'
import {isValidDate} from '@/components/commons/input-date/utils'
import {Button} from '@/components/ui/button/Button'
import {
    Dialog,
    DialogDisclosure,
    DialogDismiss,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {CheckIcon} from '@/components/ui/icon/Icon'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {
    StyledMarkAsTrasmittedDialogFooter,
    StyledMarkAsTrasmittedDialogBody
} from '@/features/projects/components/mark-as-transmitted-dialog/style'
import {useEditProjectDetails} from '@/features/projects/queries/useEditProjectDetails'
import {useBulkTransmission} from '@/features/projects/queries/useBulkTransmission'
import {ProjectProgressStatus, ProjectStatus} from '@/features/projects/types'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {useDialogContext, useDialogStore} from '@ariakit/react'
import dayjs from 'dayjs'
import {ComponentType, MouseEvent, useMemo, useState} from 'react'

export const MarkAsTrasmittedDialog = ({
    statuses,
    progressStatuses,
    projectIds,
    isOpeningTransmissionDone,
    customTrigger: CustomTrigger
}: {
    statuses: ProjectStatus[]
    progressStatuses: ProjectProgressStatus[]
    projectIds: string[]
    isOpeningTransmissionDone: boolean
    customTrigger?: ComponentType<{onClick: (event?: MouseEvent<HTMLElement>) => void; isDisabled?: boolean}>
}) => {
    const {t} = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const dialogStore = useDialogStore({
        open: isOpen,
        setOpen: setIsOpen
    })

    const isEnabled = useMemo(() => {
        return (
            statuses.every(status => status != 'opening') &&
            progressStatuses.every(progressStatus => progressStatus == 'to_transmit')
        )
    }, [statuses, progressStatuses])

    return (
        <DialogProvider store={dialogStore}>
            <DialogDisclosure
                render={
                    CustomTrigger ? (
                        <CustomTrigger onClick={() => setIsOpen(true)} isDisabled={!isEnabled} />
                    ) : (
                        <Button variant="ghost" disabled={!isEnabled}>
                            <CheckIcon />
                            {t('projects:mark_siar_transmission')}
                        </Button>
                    )
                }
            />
            <Dialog width="288px" overflowY="unset">
                <MarkAsTrasmittedDialogContent
                    projectIds={projectIds}
                    statuses={statuses}
                    isOpeningTransmissionDone={isOpeningTransmissionDone}
                />
            </Dialog>
        </DialogProvider>
    )
}

const MarkAsTrasmittedDialogContent = ({
    projectIds,
    statuses,
    isOpeningTransmissionDone
}: {
    projectIds: string[]
    statuses: ProjectStatus[]
    isOpeningTransmissionDone: boolean
}) => {
    const {t} = useTranslation()
    const dialog = useDialogContext()
    const [date, setDate] = useState<Date>(new Date())

    const isBulkTransmission = projectIds.length >= 2

    const updateProject = useEditProjectDetails({
        urlParams: {
            projectId: projectIds[0]
        },
        options: {
            onSuccess: () => {
                void queryClient.cancelQueries({
                    queryKey: [QUERY_KEYS.PROJECTS]
                })
                void queryClient.cancelQueries({
                    queryKey: [QUERY_KEYS.PROJECT, projectIds[0]]
                })
                void queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.PROJECT, projectIds[0]]
                })
                void queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.PROJECTS]
                })
                toast.success(t('projects:siar_transmission_marked'))
                dialog?.setOpen(false)
            },
            onError: () => {
                toast.error(t('errors:default'))
            }
        }
    })

    const bulkTransmission = useBulkTransmission({
        onSuccess: () => {
            toast.success(t('projects:siar_transmission_marked'))
            dialog?.setOpen(false)
        },
        onError: () => {
            toast.error(t('errors:default'))
        }
    })

    const handleConfirm = () => {
        if (isBulkTransmission) {
            const transmissionType =
                statuses.every(status => status === 'closed') || isOpeningTransmissionDone ? 'closing' : 'opening'

            bulkTransmission.mutate({
                payload: {
                    projectIds,
                    type: transmissionType,
                    transmissionDate: dayjs(date).format('YYYY-MM-DD')
                }
            })
        } else {
            if (statuses.every(status => status == 'closed') || isOpeningTransmissionDone) {
                updateProject.mutate({
                    closingTransmissionAt: dayjs(date).format('YYYY-MM-DD'),
                    progressStatus: 'transmitted'
                })
            }

            if (statuses.every(status => status == 'open')) {
                updateProject.mutate({
                    openingTransmissionAt: dayjs(date).format('YYYY-MM-DD')
                })
            }
        }
    }

    const isPending = isBulkTransmission ? bulkTransmission.isPending : updateProject.isPending

    return (
        <>
            <DialogHeader>
                <DialogHeading>{t('projects:mark_siar_transmission')}</DialogHeading>
                <DialogDismiss />
            </DialogHeader>
            <StyledMarkAsTrasmittedDialogBody>
                <InputDate
                    label={t('projects:transmission_date')}
                    onChange={date => setDate(dayjs(date).toDate())}
                    placeholder="gg/mm/aaaa"
                    value={
                        date && isValidDate(dayjs(date).format('YYYY-MM-DD'), 'YYYY-MM-DD')
                            ? dayjs(date).toDate()
                            : null
                    }
                    fullWidth
                />
            </StyledMarkAsTrasmittedDialogBody>
            <StyledMarkAsTrasmittedDialogFooter>
                <Button variant="secondary" onClick={() => dialog?.setOpen(false)}>
                    {t('commons:close')}
                </Button>
                <Button variant="primaryNeutral" onClick={handleConfirm} disabled={!date || isPending}>
                    {t('commons:confirm')}
                    {isPending && <Spinner />}
                </Button>
            </StyledMarkAsTrasmittedDialogFooter>
        </>
    )
}
